import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useFinance } from "../../../hook/useFinance";

/**
 * Actívalo cuando agregues campos de facturación en Payment (backend):
 * - document_type, invoice_series, invoice_number, invoice_date
 */
const ENABLE_PAYMENT_INVOICE_FIELDS = false;

const CURRENCIES = [
  { value: "PEN", label: "PEN" },
  { value: "USD", label: "USD" },
];

const METHODS = [
  { value: "transfer", label: "Transferencia" },
  { value: "cash", label: "Efectivo" },
  { value: "card", label: "Tarjeta" },
  { value: "deposit", label: "Depósito" },
  { value: "other", label: "Otro" },
];

const DOC_TYPES = [
  { value: "none", label: "Sin comprobante" },
  { value: "invoice", label: "Factura" },
  { value: "receipt", label: "Boleta" },
  { value: "note", label: "Nota" },
  { value: "other", label: "Otro" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const toNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export function PaymentsModal({ open, onClose, sale, onAdd, onAfterAdd }) {
  const { getPaymentsBySale } = useFinance();
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState(0);

  const [form, setForm] = useState({
    date: todayISO(),
    amount: "",
    currency: "PEN",
    method: "transfer",
    operation_number: "",
    igv_included: true,
    detraction_applied: false,
    detraction_amount: "0.00",
    notes: "",

    // factura por pago (cuando actives ENABLE_PAYMENT_INVOICE_FIELDS)
    document_type: "none",
    invoice_series: "",
    invoice_number: "",
    invoice_date: todayISO(),
  });

  const pending = useMemo(() => toNumber(sale?.pending_amount ?? 0), [sale?.pending_amount]);
  const total = useMemo(() => toNumber(sale?.total ?? 0), [sale?.total]);
  const paid = useMemo(() => toNumber(sale?.paid_amount ?? 0), [sale?.paid_amount]);

  const loadPayments = async () => {
    if (sale?.id) {
      const res = await getPaymentsBySale(sale.id);
      setPayments(res || []);
    } else {
      setPayments([]);
    }
  };

  useEffect(() => {
    if (!open) return;
    setTab(0); // abre siempre en el form
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sale?.id]);

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({
      ...f,
      date: todayISO(),
      amount: "",
      operation_number: "",
      notes: "",
      detraction_applied: false,
      detraction_amount: "0.00",
      document_type: "none",
      invoice_series: "",
      invoice_number: "",
      invoice_date: todayISO(),
    }));
  }, [sale?.id, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSwitch = (name) => (e) => {
    setForm((f) => ({ ...f, [name]: e.target.checked }));
  };

  const amountNum = useMemo(() => toNumber(form.amount), [form.amount]);

  const amountError = useMemo(() => {
    if (!form.amount) return "";
    if (amountNum <= 0) return "El monto debe ser mayor a 0.";
    if (pending > 0 && amountNum > pending) return `No puede superar el pendiente (${pending}).`;
    return "";
  }, [form.amount, amountNum, pending]);

  const canSubmit = useMemo(() => {
    if (!sale?.id) return false;
    if (!form.amount) return false;
    if (amountError) return false;
    return true;
  }, [sale?.id, form.amount, amountError]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const payload = {
      date: form.date,
      amount: amountNum,
      currency: form.currency,
      method: form.method,
      operation_number: form.operation_number,
      igv_included: Boolean(form.igv_included),
      detraction_applied: Boolean(form.detraction_applied),
      detraction_amount: toNumber(form.detraction_amount || 0),
      notes: form.notes,
    };

    // Solo si ya existe en backend
    if (ENABLE_PAYMENT_INVOICE_FIELDS) {
      payload.document_type = form.document_type;
      payload.invoice_series = form.invoice_series;
      payload.invoice_number = form.invoice_number;
      payload.invoice_date = form.invoice_date;
    }

    await onAdd(payload);

    // limpia form
    setForm((f) => ({
      ...f,
      amount: "",
      operation_number: "",
      notes: "",
    }));

    // recarga pagos y refresca ventas (para actualizar paid/pending)
    await loadPayments();
    if (onAfterAdd) await onAfterAdd();
    onClose(); // 👈 CIERRA EL MODAL
    // opcional: cambiar al tab info para que vea el pago registrado
    setTab(1);
  };

  if (!sale) return null;
  console.log(sale);
  
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">

      <DialogContent>
        <Tabs
          value={tab}
          onChange={(e, v) => {
            setTab(v);
            if (v === 1) loadPayments(); // al entrar a Info, refresca
          }}
          variant="fullWidth"
          sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}
        >
          <Tab label="Registrar pago" />
          <Tab label="Información" />
        </Tabs>

        {/* TAB 1: SOLO FORM */}
        <TabPanel value={tab} index={0}>
          <form id="paymentForm" onSubmit={submit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Fecha"
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Monto"
                  type="number"
                  name="amount"
                  value={form.amount}
                  onChange={handleChange}
                  required
                  error={Boolean(amountError)}
                  helperText={amountError || (pending > 0 ? `Máximo recomendado: ${pending}` : "")}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Moneda"
                  name="currency"
                  value={form.currency}
                  onChange={handleChange}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Método"
                  name="method"
                  value={form.method}
                  onChange={handleChange}
                >
                  {METHODS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="N° Operación (opcional)"
                  name="operation_number"
                  value={form.operation_number}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form.igv_included)}
                      onChange={handleSwitch("igv_included")}
                    />
                  }
                  label="Comprobante incluye IGV (informativo)"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form.detraction_applied)}
                      onChange={handleSwitch("detraction_applied")}
                    />
                  }
                  label="Detracción aplicada"
                />
              </Grid>

              {Boolean(form.detraction_applied) && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Monto detracción"
                    type="number"
                    name="detraction_amount"
                    value={form.detraction_amount}
                    onChange={handleChange}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notas (opcional)"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Facturación por pago (solo cuando exista en backend) */}
              {ENABLE_PAYMENT_INVOICE_FIELDS && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Comprobante del pago
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Tipo"
                      name="document_type"
                      value={form.document_type}
                      onChange={handleChange}
                    >
                      {DOC_TYPES.map((d) => (
                        <MenuItem key={d.value} value={d.value}>
                          {d.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Serie"
                      name="invoice_series"
                      value={form.invoice_series}
                      onChange={handleChange}
                      disabled={form.document_type === "none"}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Número"
                      name="invoice_number"
                      value={form.invoice_number}
                      onChange={handleChange}
                      disabled={form.document_type === "none"}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Fecha comprobante"
                      type="date"
                      name="invoice_date"
                      value={form.invoice_date}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      disabled={form.document_type === "none"}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        </TabPanel>

        {/* TAB 2: INFO */}
        <TabPanel value={tab} index={1}>
          {/* Resumen */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: "1px solid rgba(0,0,0,0.08)",
              mb: 2,
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Total
                </Typography>
                <Typography variant="h6">{total}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Pagado
                </Typography>
                <Typography variant="h6">{paid}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Pendiente
                </Typography>
                <Typography variant="h6">{pending}</Typography>
              </Grid>
            </Grid>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Historial de pagos
          </Typography>

          <Box sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 2, overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Monto</TableCell>
                  <TableCell>Moneda</TableCell>
                  <TableCell>Método</TableCell>
                  <TableCell>Operación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.date}</TableCell>
                    <TableCell>{p.amount}</TableCell>
                    <TableCell>{p.currency}</TableCell>
                    <TableCell>
                      <Chip size="small" label={p.method} />
                    </TableCell>
                    <TableCell>{p.operation_number || "-"}</TableCell>
                  </TableRow>
                ))}
                {payments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        No hay pagos registrados todavía.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        {tab === 0 && (
          <Button type="submit" form="paymentForm" variant="contained" disabled={!canSubmit}>
            Registrar pago
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
